import React, { useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuthProvider } from '../../Providers/AuthProvider'
import { useIdleTimer } from 'react-idle-timer'
import { FRONTEND_LINKS } from '../../Constants/FrontendLinks'
import TimeoutModal from './TimeoutModal'
import { useUploadIntoQueueProvider } from '../../Providers/UploadIntoQueueProvider';
import { useModalProvider } from '../../Providers/ModalProvider'
import { useEnvironmentVariablesProvider } from '../../Providers/EnvironmentVariablesProvider'
import { MobergLogoSpinner } from '../../Components/MobergLogoSpinner/MobergLogoSpinner'

const IDLE_TIMEOUT = 20 * 60 * 1000; // 20 minutes
const LOGOUT_COUNTDOWN = 2 * 60 * 1000; // 2 minutes
const TOKEN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

function isMobile() {
    const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    return regex.test(navigator.userAgent);
}

export const RequireAuth = ({ children, setNavPage }) => {
    const authProvider = useAuthProvider()
    const uploadIntoQueueProvider = useUploadIntoQueueProvider()
    const { createModal, close } = useModalProvider()
    const environmentVariablesProvider = useEnvironmentVariablesProvider()
    const navigate = useNavigate()
    const disableCheckInactivity = environmentVariablesProvider.environmentVariables.DISABLE_CHECK_INACTIVITY

    useEffect(() => {
        setNavPage(!isMobile())
    }, [setNavPage])

    // Inactivity handling
    const handleForceLogout = () => {
        authProvider.logout()
        uploadIntoQueueProvider.setOpenQueue(false)
        navigate(FRONTEND_LINKS.SUBPAGES.INACTIVE.MAIN)
    }

    const handleStayLoggedIn = () => {
        activate()
        authProvider.refreshSession().then(close)
    }

    const { isIdle, activate, isPrompted } = useIdleTimer({
        timeout: IDLE_TIMEOUT,
        promptBeforeIdle: LOGOUT_COUNTDOWN,
        // Activity in any tab in this browser keeps every tab alive; an idle
        // background tab no longer logs out the session the user is using.
        crossTab: true,
        syncTimers: 200,
        onPrompt: () => {
            if (disableCheckInactivity) return
            createModal(<TimeoutModal
                style={{ maxWidth: '90%', width: '450px' }}
                escClose={false}
                clickOutsideClose={false}
                removeModal={handleStayLoggedIn}
                logoutTime={Date.now() + LOGOUT_COUNTDOWN}
            />)
        },
        onIdle: () => {
            if (disableCheckInactivity) return
            handleForceLogout()
        },
        onActive: () => {
            // Cross-tab activity resets the timer here too — dismiss any
            // stale warning modal so the user doesn't see a stuck prompt.
            if (isPrompted()) close()
        },
    })

    // Periodic token refresh while active
    useEffect(() => {
        const checkActiveInterval = setInterval(() => {
            if (disableCheckInactivity || !isIdle()) {
                authProvider.refreshSession()
            }
        }, TOKEN_REFRESH_INTERVAL)

        return () => clearInterval(checkActiveInterval)
    }, [authProvider, isIdle, disableCheckInactivity])

    // Auth guard
    if (!authProvider.token) {
        return <Navigate to={FRONTEND_LINKS.MAIN} replace />
    }

    // Wait for permissions to be loaded before rendering children
    if (!authProvider.permissions) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <MobergLogoSpinner />
            </div>
        )
    }

    return (<>{children}</>)
}
